import { Component, ComponentInterface, h, Host, JSX, Prop, State } from '@stencil/core';
import { store } from 'app/shared/store/store';
import { getDataSpaSearchParams } from 'app/shared/utilities/helper-functions';
import { DataSpaKey } from 'app/shared/models/dataSpa';
import { MobileService } from 'app/mobile/models/mobile-service.model';
import { DataSpaSearchParams } from 'app/shared/utilities/data-spa-search-params.util';
import { StoreKeys } from 'app/shared/constants/store-keys.enum';

@Component({
  tag: 'current-plan-info',
  styleUrl: 'current-plan-info.scss',
  shadow: false,
  scoped: true
})
export class CurrentPlanInfo implements ComponentInterface {
  @Prop() public readonly dataSpa: string = null;

  @State() private isMobileLandingPage = false;
  @State() private planName = null;
  @State() private planFeature = null;
  @State() private mobileNumber = null;

  private spaParams: DataSpaSearchParams;

  public componentWillLoad(): void {
    if (this.dataSpa) {
      this.spaParams = getDataSpaSearchParams(this.dataSpa);
      this.isMobileLandingPage = this.spaParams.get(DataSpaKey.isMobile) === 'true';
    }
  }

  render(): JSX.Element {
    return (
      <Host>
        <div class="current-plan-info">
          <div class="current-plan-info__container">
            <div class="current-plan-info__selection">
              <div class="current-plan-info__selection__message" data-testid="current-plan-title">
                <h2>Change your {this.isMobileLandingPage ? 'SIM' : 'nbn®'} plan</h2>
              </div>
            </div>
            <div class="current-plan-info__header">Your current plan</div>
            {!this.isMobileLandingPage && this.renderCurrentNbnPlanInfo()}
            {this.isMobileLandingPage && this.renderCurrentMobilePlanInfo()}
            <div class="current-plan-info__disclaimer">Any offers included in your current plan will end.</div>
          </div>
        </div>
      </Host>
    );
  }

  renderCurrentNbnPlanInfo(): JSX.Element {
    const currentPlan = store.get(StoreKeys.currentNbnPlan);
    const address = store.get('address');
    let serviceAddress = '';
    if (currentPlan) {
      this.planName = currentPlan?.name;
      this.planFeature = currentPlan?.content?.features?.find(
        (feature) => feature.featureDescription.indexOf('Mbps') > 0
      )?.featureDescription;
    }

    if (address) {
      serviceAddress = address.fullAddress;
    }

    return (
      store.get(StoreKeys.currentNbnPlan) && (
        <div class="current-plan-info-details">
          <div class="current-plan-info-details__container">
            <div class="current-plan-info-details__item">
              <div class="current-plan-info-details__title">
                <img class="current-plan-info-details__icon" src="/content/dam/digital/agl/icons/blue/home_icon_blue.svg" />
                <div class="current-plan-info-details__text" data-testid="current-plan-name">
                  {this.planName}
                </div>
              </div>
              <div class="current-plan-info-details__info" data-testid="selected-address">
                {serviceAddress}
              </div>
            </div>
            {this.planFeature && (
              <div class="current-plan-info-details__item">
                <div class="current-plan-info-details__title">
                  <img class="current-plan-info-details__icon" src="/content/dam/digital/agl/icons/blue/agl_icon_app_nbn.svg" />
                  <div class="current-plan-info-details__text">Speed</div>
                </div>
                <div class="current-plan-info-details__info" data-testid="current-plan-feature">
                  {this.planFeature}
                </div>
              </div>
            )}
          </div>
        </div>
      )
    );
  }

  renderCurrentMobilePlanInfo(): JSX.Element {
    const existingMobileService = store.get('selectedTelcoService') as MobileService;
    const currentMobilePlan = store.get(StoreKeys.currentMobilePlan);
    if (currentMobilePlan && existingMobileService) {
      this.planName = currentMobilePlan.name;
      if (existingMobileService.mobileServiceNumber)
        this.mobileNumber = this.formatAustralianMobileNumber(existingMobileService.mobileServiceNumber);
    }

    return (
      store.get(StoreKeys.currentMobilePlan) && (
        <div class="current-plan-info-details">
          <div class="current-plan-info-details__container">
            <div class="current-plan-info-details__item">
              <div class="current-plan-info-details__title">
                <img class="current-plan-info-details__icon" src="/content/dam/digital/agl/icons/blue/generic/icon-sim-pencil.svg" />
                <div class="current-plan-info-details__text">Plan Type</div>
              </div>
              <div class="current-plan-info-details__info" data-testid="current-plan-name">
                {this.planName} Plan
              </div>
            </div>
            <div class="current-plan-info-details__item">
              <div class="current-plan-info-details__title">
                <img class="current-plan-info-details__icon" src="/content/dam/digital/agl/icons/agl_icon_mobile_plans_24px.svg" />
                <div class="current-plan-info-details__text">Phone Number</div>
              </div>
              <div class="current-plan-info-details__info" data-testid="current-mobile">
                {this.mobileNumber}
              </div>
            </div>
          </div>
        </div>
      )
    );
  }

  private formatAustralianMobileNumber(number: string): string {
    // Remove the country code '61' and add '0' at the beginning
    if (number.startsWith('61')) {
      number = `0${number.slice(2)}`;
    }

    // Format the number as '0400 123 123'
    return number.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');
  }
}
